/*
  # Simple Docket Autocomplete Component

  A lightweight, crash-resistant autocomplete component for docket number search
  with proper error handling and fallback mechanisms.
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader, AlertCircle, FileText, X } from 'lucide-react';

interface DocketSuggestion {
  docketNumber: string;
  displayText: string;
  description?: string;
}

interface SimpleDocketAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  debounceMs?: number;
  minQueryLength?: number;
  maxSuggestions?: number;
}

export const SimpleDocketAutocomplete: React.FC<SimpleDocketAutocompleteProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Enter docket number or description (e.g., 21-402, Digital TV, 5G)",
  disabled = false,
  className = "",
  showIcon = true,
  debounceMs = 500,
  minQueryLength = 2,
  maxSuggestions = 8
}) => {
  const [suggestions, setSuggestions] = useState<DocketSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  // Safe API call with timeout and error handling
  const fetchSuggestions = useCallback(async (query: string): Promise<DocketSuggestion[]> => {
    if (!query || query.trim().length < minQueryLength) {
      return [];
    }

    try {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller with timeout
      abortControllerRef.current = new AbortController();
      const timeoutId = setTimeout(() => {
        abortControllerRef.current?.abort();
      }, 10000); // 10 second timeout

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search-docket-suggestions`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: query.trim(), 
          limit: maxSuggestions 
        }),
        signal: abortControllerRef.current.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      return data.suggestions || [];

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return []; // Request was cancelled, return empty array
      }
      
      console.warn('Autocomplete API error:', err);
      return []; // Return empty array on any error
    }
  }, [minQueryLength, maxSuggestions]);

  // Debounced search
  const debouncedSearch = useCallback((query: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      if (query.trim().length < minQueryLength) {
        setSuggestions([]);
        setShowSuggestions(false);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const results = await fetchSuggestions(query);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setSelectedIndex(-1);
      } catch (err) {
        setError('Failed to load suggestions');
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);
  }, [fetchSuggestions, debounceMs, minQueryLength]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    debouncedSearch(newValue);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: DocketSuggestion) => {
    onChange(suggestion.docketNumber);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSubmit?.(suggestion.docketNumber);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim()) {
      onSubmit?.(value.trim());
      setShowSuggestions(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && value.trim()) {
        onSubmit?.(value.trim());
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;

      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (value.trim()) {
          onSubmit?.(value.trim());
          setShowSuggestions(false);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle input blur
  const handleBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          {showIcon && (
            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full ${showIcon ? 'pl-12' : 'pl-4'} pr-14 py-3 text-base backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg text-black placeholder-gray-500 focus:bg-white focus:border-green-500 focus:outline-none transition-all duration-300 disabled:opacity-50 shadow-sm`}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={!value.trim() || disabled}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-green-500 hover:bg-green-600 border border-green-600 rounded-lg text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {error ? (
            <div className="p-3 text-red-600 text-sm flex items-center space-x-2">
              <AlertCircle className="w-4 h-4" />
              <span>Unable to load suggestions</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto p-1 hover:bg-red-50 rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-3 text-gray-500 text-sm">
              No suggestions found
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.docketNumber}-${index}`}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 ${
                  index === selectedIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                } ${index === 0 ? 'rounded-t-lg' : ''} ${
                  index === suggestions.length - 1 ? 'rounded-b-lg' : 'border-b border-gray-100'
                }`}
              >
                <div className="flex items-start space-x-2">
                  <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{suggestion.docketNumber}</div>
                    {suggestion.description && (
                      <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {suggestion.description}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
